<!-- Page Header -->
<div class="d-flex justify-content-between align-items-start mb-4">
  <div>
    <h1 class="h2 mb-1 fw-bold">Work Orders</h1>
    <p class="text-muted mb-0">Schedule and assign work for your pilots.</p>
  </div>
  <button class="btn btn-primary" (click)="newWorkOrder()">
    <i class="bi bi-plus-lg me-2"></i>New Work Order
  </button>
</div>

<!-- Status chips -->
<div class="d-flex flex-wrap gap-2 mb-3">
  <button
    *ngFor="let opt of statusOptions"
    type="button"
    class="btn btn-sm"
    [class.btn-primary]="statusFilter() === opt.value"
    [class.btn-outline-secondary]="statusFilter() !== opt.value"
    (click)="statusFilter.set(opt.value)"
  >
    {{ opt.label }}
    <span class="badge ms-1"
          [class.bg-light]="statusFilter() === opt.value"
          [class.text-primary]="statusFilter() === opt.value"
          [class.bg-secondary]="statusFilter() !== opt.value">
      {{ statusCounts()[opt.value] }}
    </span>
  </button>
</div>

<!-- Filters -->
<div class="card mb-4">
  <div class="card-body p-3">
    <div class="row g-3 align-items-end">
      <div class="col-md-3">
        <label class="form-label">Assigned pilot</label>
        <select class="form-select" [ngModel]="assignedFilter()" (ngModelChange)="assignedFilter.set($event)">
          <option value="all">All pilots</option>
          <option value="unassigned">Unassigned</option>
          <option *ngFor="let p of assignablePilots()" [value]="p.id">{{ p.name }}</option>
        </select>
      </div>
      <div class="col-md-3">
        <label class="form-label">Customer</label>
        <select class="form-select" [ngModel]="customerFilter()" (ngModelChange)="customerFilter.set($event)">
          <option value="all">All customers</option>
          <option *ngFor="let c of customers()" [value]="c.id">{{ c.name }}</option>
        </select>
      </div>
      <div class="col-md-2">
        <label class="form-label">From</label>
        <input type="date" class="form-control"
               [ngModel]="dateFrom()" (ngModelChange)="dateFrom.set($event)">
      </div>
      <div class="col-md-2">
        <label class="form-label">To</label>
        <input type="date" class="form-control"
               [ngModel]="dateTo()" (ngModelChange)="dateTo.set($event)">
      </div>
      <div class="col-md-2">
        <button class="btn btn-outline-secondary w-100" (click)="clearFilters()">
          <i class="bi bi-x-lg me-1"></i>Clear
        </button>
      </div>
      <div class="col-12">
        <label class="form-label">Search</label>
        <div class="input-group">
          <span class="input-group-text"><i class="bi bi-search"></i></span>
          <input type="text" class="form-control" placeholder="Search by title, field, customer, or pilot..."
                 [ngModel]="searchQuery()" (ngModelChange)="searchQuery.set($event)">
        </div>
      </div>
    </div>
  </div>
</div>

<!-- Table -->
<div class="card">
  <div class="card-header d-flex justify-content-between align-items-center p-3">
    <h5 class="mb-0">Work Orders</h5>
    <span class="badge bg-secondary">{{ filtered().length }} of {{ workOrders().length }}</span>
  </div>
  <div class="card-body p-0">
    <ng-container *ngIf="filtered().length > 0; else emptyState">
      <div class="table-responsive">
        <table class="table table-hover mb-0">
          <thead>
            <tr>
              <th>Status</th>
              <th>Title</th>
              <th>Customer</th>
              <th>Assigned</th>
              <th>Scheduled</th>
              <th>Source</th>
            </tr>
          </thead>
          <tbody>
            <tr *ngFor="let wo of filtered()" class="cursor-pointer" (click)="view(wo)">
              <td>
                <span [ngClass]="statusBadgeClass(wo.status)">
                  {{ statusLabel(wo.status) }}
                </span>
              </td>
              <td>
                <div class="fw-medium">{{ wo.title || 'Untitled work order' }}</div>
                <div class="small text-muted" *ngIf="wo.fieldName || acresDisplay(wo) !== null || wo.cropType || locationDisplay(wo)">
                  <ng-container *ngIf="wo.fieldName">{{ wo.fieldName }}</ng-container>
                  <ng-container *ngIf="acresDisplay(wo) !== null">
                    <span class="mx-1">·</span>{{ acresDisplay(wo) | number:'1.1-1' }} ac
                  </ng-container>
                  <ng-container *ngIf="wo.cropType">
                    <span class="mx-1">·</span>{{ wo.cropType }}
                  </ng-container>
                  <ng-container *ngIf="locationDisplay(wo)">
                    <span class="mx-1">·</span>{{ locationDisplay(wo) }}
                  </ng-container>
                </div>
              </td>
              <td>{{ wo.customerName || '—' }}</td>
              <td>
                <span *ngIf="wo.assignedPilotProfileId; else unassigned">
                  {{ pilotName(wo) }}
                </span>
                <ng-template #unassigned>
                  <span class="text-muted">Unassigned</span>
                </ng-template>
              </td>
              <td>
                <ng-container *ngIf="wo.scheduledDate; else nodate">
                  {{ wo.scheduledDate | date:'mediumDate' }}
                  <span *ngIf="wo.scheduledTime" class="text-muted small">
                    • {{ wo.scheduledTime }}
                  </span>
                </ng-container>
                <ng-template #nodate>
                  <span class="text-muted">—</span>
                </ng-template>
              </td>
              <td>
                <span class="badge"
                      [class.bg-info-subtle]="wo.jobId"
                      [class.text-info]="wo.jobId"
                      [class.bg-light]="!wo.jobId"
                      [class.text-secondary]="!wo.jobId">
                  {{ sourceLabel(wo) }}
                </span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </ng-container>

    <ng-template #emptyState>
      <div class="p-5 text-center">
        <div class="empty-state-icon mb-3">
          <i class="bi bi-clipboard-check"></i>
        </div>
        <h5>No Work Orders</h5>
        <p class="text-muted mb-3" *ngIf="workOrders().length === 0">
          Schedule your first job — assign a pilot, set a date, and you're off.
        </p>
        <p class="text-muted mb-3" *ngIf="workOrders().length > 0">
          No work orders match your filters. Adjust the criteria above.
        </p>
        <button class="btn btn-primary" (click)="newWorkOrder()" *ngIf="workOrders().length === 0">
          <i class="bi bi-plus-lg me-2"></i>New Work Order
        </button>
        <button class="btn btn-outline-secondary" (click)="clearFilters()" *ngIf="workOrders().length > 0">
          <i class="bi bi-x-lg me-2"></i>Clear filters
        </button>
      </div>
    </ng-template>
  </div>
</div>
